import { useState, useEffect } from 'react';
import { ArrowLeft, DollarSign, Download, TrendingUp, Calendar, Clock, ArrowUpRight, AlertCircle, Loader2 } from 'lucide-react';
import { EarningsCard } from '../EarningsCard';
import { walletService, Earning, Withdrawal } from '../../services/wallet.service';
import { dbService } from '../../services/firebase-db.service';
import { auth } from '../../config/firebase.config';

interface Transaction {
  id: string;
  type: 'delivery' | 'bonus' | 'tip' | 'withdrawal' | 'quest_reward' | 'referral';
  amount: string;
  description: string;
  date: string;
  time: string;
}

interface EarningsScreenProps {
  onBack: () => void;
  riderId?: string; // Current logged-in rider ID
}

export function EarningsScreen({ onBack, riderId = 'demo-rider-123' }: EarningsScreenProps) {
  const [balance, setBalance] = useState('₦0');
  const [todayEarnings, setTodayEarnings] = useState('₦0');
  const [weekEarnings, setWeekEarnings] = useState('₦0');
  const [monthEarnings, setMonthEarnings] = useState('₦0');
  const [pendingAmount, setPendingAmount] = useState('₦0');
  const [availableBalance, setAvailableBalance] = useState('₦0');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedDeliveries, setCompletedDeliveries] = useState(0);
  const [bikeEarnings, setBikeEarnings] = useState('₦0');
  const [bicycleEarnings, setBicycleEarnings] = useState('₦0');
  const [legEarnings, setLegEarnings] = useState('₦0');
  

  // Load wallet data
  useEffect(() => {
    loadWalletData();
  }, [riderId]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get wallet balance
      const walletBalance = await walletService.getWalletBalance(riderId);
      setBalance(`₦${walletBalance.total.toLocaleString()}`);
      setTodayEarnings(`₦${walletBalance.today.toLocaleString()}`);
      setWeekEarnings(`₦${walletBalance.week.toLocaleString()}`);
      setMonthEarnings(`₦${walletBalance.month.toLocaleString()}`);
      setPendingAmount(`₦${walletBalance.pending.toLocaleString()}`);
      setAvailableBalance(`₦${walletBalance.available.toLocaleString()}`);
      
      // Get rider stats including completed deliveries and vehicle type earnings
      const riderStats = await walletService.getRiderStats(riderId);
      setCompletedDeliveries(riderStats.completedDeliveries);
      setBikeEarnings(`₦${riderStats.bikeEarnings.toLocaleString()}`);
      setBicycleEarnings(`₦${riderStats.bicycleEarnings.toLocaleString()}`);
      setLegEarnings(`₦${riderStats.legEarnings.toLocaleString()}`);

      // Get earning history
      const earnings = await walletService.getEarningHistory(riderId, 20);
      
      // Get withdrawal history
      const withdrawals = await walletService.getWithdrawalHistory(riderId, 10);

      // Combine and format transactions
      const earningTransactions: Transaction[] = earnings.map(e => ({
        id: e.id,
        type: e.type,
        amount: `₦${e.amount.toLocaleString()}`,
        description: e.description,
        date: formatDate(e.createdAt),
        time: formatTime(e.createdAt),
      }));

      const withdrawalTransactions: Transaction[] = withdrawals.map(w => ({
        id: w.id,
        type: 'withdrawal',
        amount: `₦${w.amount.toLocaleString()}`,
        description: `Withdrawal · ${w.bankName} ${w.accountNumber} (${w.status})`,
        date: formatDate(w.requestedAt),
        time: formatTime(w.requestedAt),
      }));

      // Combine — earnings and withdrawals are already ordered desc from Firestore; just interleave
      const allTransactions = [...earningTransactions, ...withdrawalTransactions];

      setTransactions(allTransactions);
    } catch (err) {
      console.error('Error loading wallet data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const onWithdraw = async () => {
    if (withdrawing) return;

    const availableNum = parseFloat(availableBalance.replace(/[₦,]/g, ''));

    if (availableNum < 1000) {
      alert('Minimum withdrawal amount is ₦1,000');
      return;
    }

    const amountStr = prompt(`Enter withdrawal amount (Available: ${availableBalance}):`);
    if (!amountStr) return;

    const amount = parseFloat(amountStr.replace(/[₦,]/g, ''));

    if (isNaN(amount) || amount <= 0) {
      alert('Invalid amount');
      return;
    }
    if (amount < 1000) {
      alert('Minimum withdrawal amount is ₦1,000');
      return;
    }
    if (amount > availableNum) {
      alert('Insufficient balance');
      return;
    }

    try {
      setWithdrawing(true);

      // Load real bank details from rider profile
      const user = auth.currentUser;
      let bankDetails = { bankName: '', accountNumber: '', accountName: '' };
      if (user) {
        const profile = await dbService.getRiderProfile(user.uid);
        if (profile?.bankDetails) {
          bankDetails = {
            bankName: profile.bankDetails.bankName || '',
            accountNumber: profile.bankDetails.accountNumber || '',
            accountName: profile.bankDetails.accountName || '',
          };
        }
      }

      if (!bankDetails.accountNumber) {
        alert('⚠️ No bank account on file. Please update your profile with bank details first.');
        setWithdrawing(false);
        return;
      }

      await walletService.requestWithdrawal(riderId, amount, bankDetails);

      alert(
        `✅ Withdrawal request submitted!\n` +
        `Amount: ₦${amount.toLocaleString()}\n` +
        `Bank: ${bankDetails.bankName}\n` +
        `Account: ${bankDetails.accountNumber}\n` +
        `Processing time: 1-2 business days`
      );

      await loadWalletData();
    } catch (err) {
      console.error('Withdrawal error:', err);
      alert('❌ ' + (err instanceof Error ? err.message : 'Withdrawal failed'));
    } finally {
      setWithdrawing(false);
    }
  };

  const canWithdraw = parseFloat(availableBalance.replace(/[₦,]/g, '')) >= 1000;
  

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#E0001B] animate-spin mx-auto mb-2" />
          <p className="text-[#6B6B6B]">Loading wallet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute inset-0 bg-gray-50 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
            <ArrowLeft className="w-6 h-6 text-[#111111]" />
          </button>
          <h1 className="text-[18px] text-[#111111]">Earnings & Wallet</h1>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-[18px] font-semibold text-[#111111] mb-2">Failed to Load</h2>
            <p className="text-[14px] text-[#6B6B6B] mb-4">{error}</p>
            <button
              onClick={loadWalletData}
              className="px-6 py-2 bg-[#E0001B] text-white rounded-lg hover:bg-[#C00017]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <h1 className="text-[18px] text-[#111111]">Earnings & Wallet</h1>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4">
        {/* Balance Card */}
        <div className="bg-gradient-to-br from-[#E0001B] to-[#C00017] rounded-[12px] p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-white/80 text-[14px] mb-1">AVAILABLE BALANCE</p>
              <p className="text-[32px]">{availableBalance}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
          
          <button
            onClick={onWithdraw}
            disabled={!canWithdraw || withdrawing}
            className="w-full h-12 rounded-[12px] bg-white text-[#E0001B] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {withdrawing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Withdraw Funds
              </>
            )}
          </button>
          
          {!canWithdraw && (
            <p className="text-white/70 text-[12px] mt-2 text-center">
              Minimum withdrawal: ₦1,000
            </p>
          )}

          {parseFloat(pendingAmount.replace(/[₦,]/g, '')) > 0 && (
            <div className="mt-3 p-3 bg-white/10 rounded-lg border border-white/20">
              <p className="text-white/80 text-[11px]">PENDING WITHDRAWALS</p>
              <p className="text-white text-[16px] font-semibold">{pendingAmount}</p>
            </div>
          )}
        </div>

        {/* Earnings Summary */}
        <div className="bg-gradient-to-br from-[#E0001B] to-[#C00017] rounded-[12px] p-4 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-white/80 text-[12px] mb-1">Today's Earnings</p>
              <p className="text-white text-[28px] font-bold">{todayEarnings}</p>
            </div>
            <div className="p-2 rounded-lg bg-white/10">
              <DollarSign className="w-5 h-5 text-white/90" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-white/90">
            <div>
              <p className="text-[18px] font-bold">{weekEarnings}</p>
              <p className="text-[11px] opacity-70">This Week</p>
            </div>
            <div>
              <p className="text-[18px] font-bold">{monthEarnings}</p>
              <p className="text-[11px] opacity-70">This Month</p>
            </div>
            <div>
              <p className="text-[18px] font-bold">{completedDeliveries}</p>
              <p className="text-[11px] opacity-70">Deliveries</p>
            </div>
          </div>
        </div>

        {/* Earnings by Vehicle Type */}
        <div className="bg-white rounded-[12px] p-4 border border-gray-100">
          <h3 className="text-[16px] text-[#111111] mb-3">Earnings by Vehicle Type</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                  <span className="text-[14px]">🏍️</span>
                </div>
                <span className="text-[14px] text-[#111111]">Bike</span>
              </div>
              <span className="text-[16px] font-semibold text-[#111111]">{bikeEarnings}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#0066CC]/10 flex items-center justify-center">
                  <span className="text-[14px]">🚴</span>
                </div>
                <span className="text-[14px] text-[#111111]">Bicycle</span>
              </div>
              <span className="text-[16px] font-semibold text-[#111111]">{bicycleEarnings}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#00B277]/10 flex items-center justify-center">
                  <span className="text-[14px]">🚶</span>
                </div>
                <span className="text-[14px] text-[#111111]">Leg Fleet</span>
              </div>
              <span className="text-[16px] font-semibold text-[#111111]">{legEarnings}</span>
            </div>
          </div>
        </div>

        {/* Total Stats Summary */}
        <div className="bg-white rounded-[12px] p-4 border border-gray-100">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[12px] text-[#6B6B6B] mb-1">Total Deliveries</p>
              <p className="text-[24px] font-bold text-[#111111]">{completedDeliveries}</p>
            </div>
            <div>
              <p className="text-[12px] text-[#6B6B6B] mb-1">Pending Payouts</p>
              <p className="text-[24px] font-bold text-[#E0001B]">{pendingAmount}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-[12px] p-3 border border-gray-100 text-center">
            <div className="w-10 h-10 rounded-full bg-[#00B277]/10 flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="w-5 h-5 text-[#00B277]" />
            </div>
            <p className="text-[18px] text-[#111111]">+12%</p>
            <p className="text-[12px] text-[#6B6B6B]">vs last week</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 border border-gray-100 text-center">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-[18px] text-[#111111]">24</p>
            <p className="text-[12px] text-[#6B6B6B]">Days active</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 border border-gray-100 text-center">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-[18px] text-[#111111]">87h</p>
            <p className="text-[12px] text-[#6B6B6B]">This month</p>
          </div>
        </div>

        {/* Payout Schedule */}
        <div className="bg-white rounded-[12px] p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[16px] text-[#111111]">Payout Schedule</h3>
            <button className="text-[14px] text-[#E0001B] hover:underline">
              Change
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-[14px] text-[#111111]">Daily Settlement</p>
              <p className="text-[12px] text-[#6B6B6B]">Funds available for withdrawal daily</p>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-[12px] border border-gray-100">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-[16px] text-[#111111]">Recent Transactions</h3>
            <button className="text-[14px] text-[#E0001B] hover:underline">
              View All
            </button>
          </div>
          
          <div className="divide-y divide-gray-100">
            {transactions.slice(0, 10).map((transaction) => {
              const isPositive = transaction.type !== 'withdrawal';
              const getIcon = () => {
                switch (transaction.type) {
                  case 'delivery':
                    return <DollarSign className="w-4 h-4 text-[#00B277]" />;
                  case 'bonus':
                    return <TrendingUp className="w-4 h-4 text-[#FFB020]" />;
                  case 'tip':
                    return <ArrowUpRight className="w-4 h-4 text-blue-600" />;
                  case 'withdrawal':
                    return <Download className="w-4 h-4 text-[#E0001B]" />;
                  default:
                    return <DollarSign className="w-4 h-4" />;
                }
              };

              return (
                <div key={transaction.id} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    transaction.type === 'delivery' ? 'bg-[#00B277]/10' :
                    transaction.type === 'bonus' ? 'bg-[#FFB020]/10' :
                    transaction.type === 'tip' ? 'bg-blue-100' :
                    'bg-[#E0001B]/10'
                  }`}>
                    {getIcon()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-[#111111] truncate">{transaction.description}</p>
                    <p className="text-[12px] text-[#6B6B6B]">{transaction.date} • {transaction.time}</p>
                  </div>
                  <p className={`text-[16px] ${isPositive ? 'text-[#00B277]' : 'text-[#E0001B]'}`}>
                    {isPositive ? '+' : '-'}{transaction.amount}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}